import { useCallback, useEffect, useState } from "react";
import { guildApi, type GuildTreeStateResponse } from "../api/guild";
import { useGameStore } from "../store/useGameStore";
import { GuildTreeNodeGrid } from "../components/guild/GuildTreeNodeGrid";

export function GuildTreeScreen() {
  const { setScreen } = useGameStore();
  const [state, setState] = useState<GuildTreeStateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [gatheringNode, setGatheringNode] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await guildApi.treeState();
      setState(data);
      setError(null);
      if (data.active) {
        await guildApi.treeJoin().catch(() => undefined);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load tree");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!state?.active) return;
    const timer = setInterval(() => {
      void guildApi.treeHeartbeat().then(setState).catch(() => undefined);
    }, 1000);
    return () => clearInterval(timer);
  }, [state?.active]);

  useEffect(() => {
    return () => {
      void guildApi.treeLeave().catch(() => undefined);
    };
  }, []);

  const handleStart = async () => {
    setActionLoading(true);
    setMessage(null);
    try {
      const data = await guildApi.treeStart();
      setState(data);
      setMessage("Guild Tree started — 30 minutes of bonuses!");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not start tree");
    } finally {
      setActionLoading(false);
    }
  };

  const handleTap = async (nodeIndex: number) => {
    if (actionLoading || gatheringNode !== null) return;
    setActionLoading(true);
    setError(null);
    try {
      const res = await guildApi.treeTap(nodeIndex);
      setGatheringNode(nodeIndex);
      const parts = Object.entries(res.rewards)
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${k} +${v}`)
        .join(" · ");
      setMessage(parts ? `Collected: ${parts}` : "Spark collected!");
      setTimeout(() => setGatheringNode(null), (res.gather_display_sec ?? 5) * 1000);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Tap missed");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4 pb-10">
      <button type="button" onClick={() => setScreen("guild")} className="text-sm text-gray-400 hover:text-white">
        ← Back to Guild
      </button>

      <div className="text-center space-y-1">
        <h2 className="text-xl font-bold font-game text-emerald-300">🌳 Guild Tree</h2>
        <p className="text-gray-400 text-sm">Passive ticks every 30s. Tap lit nodes within {state?.tap_window_sec ?? 3}s for bonus rewards.</p>
      </div>

      {loading && <p className="text-center text-gray-500 text-sm">Loading…</p>}
      {error && <p className="text-center text-red-400 text-sm">{error}</p>}
      {message && <p className="text-center text-emerald-300 text-sm">{message}</p>}

      {state && (
        <>
          <div className="bg-kingdom-purple border border-kingdom-mid rounded-xl p-3 text-sm space-y-1">
            {state.active ? (
              <>
                <p className="text-emerald-200 font-semibold">Event active</p>
                <p className="text-gray-400">Time left: {state.seconds_remaining}s</p>
              </>
            ) : (
              <p className="text-gray-300">No active tree session.</p>
            )}
            {state.can_start && (
              <button
                type="button"
                disabled={actionLoading}
                onClick={() => void handleStart()}
                className="mt-2 w-full py-2 rounded-lg bg-emerald-700 hover:bg-emerald-600 font-semibold disabled:opacity-50"
              >
                Start Guild Tree (leader)
              </button>
            )}
            {!state.can_start && !state.active && (
              <p className="text-xs text-gray-500">
                Starts today: {state.starts_today}/{state.max_starts_per_day}
              </p>
            )}
          </div>

          {state.active && (
            <GuildTreeNodeGrid
              nodeCount={state.node_count}
              activeNode={state.active_node}
              tapWindowSec={state.tap_window_sec}
              disabled={actionLoading}
              gatheringNode={gatheringNode}
              onTap={(idx) => void handleTap(idx)}
            />
          )}

          <div className="bg-kingdom-dark/80 border border-kingdom-mid rounded-xl p-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Present ({state.attendees.length})</p>
            <ul className="space-y-1 max-h-40 overflow-y-auto">
              {state.attendees.map((a) => (
                <li key={a.user_id} className="text-sm text-gray-200 truncate">
                  {a.display_name}
                </li>
              ))}
              {state.attendees.length === 0 && (
                <li className="text-sm text-gray-500">No one here yet — open this screen to join.</li>
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
